import { useState } from 'react';
import { Button } from './common/inputs/Button';
import Modal from 'react-modal';
import { faClose } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TextField } from '@mui/material';
import { invalid, label, textarea } from './legacyForms.css';
import * as styles from './Faq.css';
import { clsx } from 'clsx';

type QA = {
  question: string;
  answer: string;
};
type FaqProps = {
  faqSuggestions?: Array<QA>;
  questions: Array<QA>;
  onChange: any;
  tree: any;
  questionPlaceholder?: string;
  answerPlaceholder?: string;
  isSalesPageBuilder?: boolean;
  maxItems?: number;
  addLabel?: string;
};

const Faq = (props: FaqProps) => {
  const [faqSuggestionsModal, setFaqSuggestionsModal] = useState(false);

  const onChange = (index, data) => {
    let questions = props.questions.slice();
    questions[index] = data;
    props.onChange(questions);
  };

  const moveUp = (index) => {
    if (index < 1) return;
    let questions = props.questions.slice();
    let t = questions[index];
    questions[index] = questions[index - 1];
    questions[index - 1] = t;
    props.onChange(questions);
  };

  const moveDown = (index) => {
    if (index > props.questions.length - 1) return;
    let questions = props.questions.slice();
    let t = questions[index + 1];
    questions[index + 1] = questions[index];
    questions[index] = t;
    props.onChange(questions);
  };

  const deleteItem = (index) => {
    let questions = props.questions.slice();
    questions.splice(index, 1);
    props.onChange(questions);
  };

  const add = (
    faq = {
      question: '',
      answer: '',
    },
  ) => {
    let questions = props.questions.slice();
    questions.push(faq);
    props.onChange(questions);
  };

  const handleSuggestionSelect = (faq: QA) => {
    add(faq);
    toggleSuggestionModal(false);
  };

  const toggleSuggestionModal = (value: boolean) => {
    setFaqSuggestionsModal(value);
  };

  const enableFaqSuggestions = (props.faqSuggestions?.length ?? 0) > 0;

  const canAddFaq =
    !props.maxItems || props.maxItems > (props.questions?.length ?? 0);

  return (
    <section>
      {faqSuggestionsModal && (
        <FaqSuggestionsModal
          faqSuggestions={props.faqSuggestions}
          onClose={() => toggleSuggestionModal(false)}
          onSuggestionSelect={handleSuggestionSelect}
        />
      )}
      {(props.questions?.length ?? 0) > 0 &&
        props.questions.map((q, i) => (
          <FaqItem
            question={q.question}
            answer={q.answer}
            key={i}
            total={props.questions.length}
            index={i}
            onChange={onChange}
            onMoveUp={moveUp}
            onMoveDown={moveDown}
            onDelete={deleteItem}
            questionPlaceholder={
              props.questionPlaceholder
                ? props.questionPlaceholder
                : 'Question?'
            }
            answerPlaceholder={
              props.answerPlaceholder ? props.answerPlaceholder : 'Answer.'
            }
            isSalesPageBuilder={props.isSalesPageBuilder}
            tree={props.tree && props.tree[i] ? props.tree[i] : null}
          />
        ))}

      <div className="flex gap-4">
        {canAddFaq && (
          <Button onClick={add}>
            {props.addLabel ? props.addLabel : '+ Add'}
          </Button>
        )}
        {canAddFaq && enableFaqSuggestions && (
          <Button onClick={() => toggleSuggestionModal(true)} variant="gray">
            Show FAQ suggestions
          </Button>
        )}
      </div>
    </section>
  );
};

function FaqSuggestionsModal({ faqSuggestions, onClose, onSuggestionSelect }) {
  return (
    <Modal
      isOpen={true}
      onRequestClose={onClose}
      style={{
        overlay: {
          zIndex: 100,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        },
        content: {
          padding: '30px',
          backgroundColor: 'white',
          position: 'relative',
          maxWidth: '1000px',
          maxHeight: '80vh',
          overflowY: 'scroll',
          height: 'fit-content',
          borderRadius: '20px',
        },
      }}
    >
      <a
        onClick={onClose}
        className="absolute right-8 text-3xl text-black hover:text-primary hover:cursor-pointer"
      >
        <FontAwesomeIcon icon={faClose} />
      </a>
      <h2 className="mb-10">FAQ suggestions</h2>
      {faqSuggestions.map((faq, index) => (
        <FaqSuggestion
          key={index}
          faq={faq}
          onSelect={() => onSuggestionSelect(faq)}
        />
      ))}
    </Modal>
  );
}

function FaqSuggestion({ faq, onSelect }) {
  return (
    <div className="relative mb-5 border-b last:border-b-0">
      <Button className="absolute right-0" onClick={onSelect}>
        Use FAQ
      </Button>
      <div className="text-[17px] font-bold">{faq.question}</div>
      <div className="mt-5 mb-5 text-[15px] leading-[1.5] opacity-80 whitespace-pre-wrap pr-[150px]">
        {faq.answer}
      </div>
    </div>
  );
}

const FaqItem = (props) => {
  const changeQuestion = (ev) => {
    props.onChange(props.index, {
      question: ev.target.value,
      answer: props.answer,
    });
  };
  const changeAnswer = (ev) => {
    props.onChange(props.index, {
      question: props.question,
      answer: ev.target.value,
    });
  };
  const validate = (attribute) => {
    if (
      props.tree &&
      props.tree[attribute] &&
      props.tree[attribute].length > 0
    ) {
      return invalid;
    }
    return;
  };

  let buttons: JSX.Element[] = [];
  if (props.index > 0) {
    if (props.index < props.total - 1) {
      buttons = [
        <a
          className={clsx(styles.moveIcon, styles.iconArrowUp)}
          onClick={() => props.onMoveUp(props.index)}
        />,
        <a
          className={clsx(styles.moveIcon, styles.iconArrowDown)}
          onClick={() => props.onMoveDown(props.index)}
        />,
      ];
    } else {
      buttons = [
        <div
          style={{
            display: 'inline-block',
            width: '25px',
            height: '25px',
            marginLeft: '10px',
          }}
        />,
        <a
          className={clsx(styles.moveIcon, styles.iconArrowUp)}
          onClick={() => props.onMoveUp(props.index)}
        />,
      ];
    }
  } else if (props.total === 1) {
    buttons = [];
  } else {
    buttons = [
      <div
        style={{
          display: 'inline-block',
          width: '25px',
          height: '25px',
          marginLeft: '10px',
        }}
      />,
      <a
        className={clsx(styles.moveIcon, styles.iconArrowDown)}
        onClick={() => props.onMoveDown(props.index)}
      />,
    ];
  }

  return (
    <div>
      <div
        className={clsx(
          'items-stretch',
          props.isSalesPageBuilder
            ? 'flex flex-col max-w-[1000px] md:flex-row'
            : 'flex flex-row',
        )}
      >
        <div
          style={{ /*background: '#f00',*/ flexGrow: '10', flexShrink: '10' }}
        >
          <div className={styles.field}>
            {props.isSalesPageBuilder ? (
              <>
                <h4>FAQ {props.index + 1}</h4>
                <label className={label}>
                  Question
                  <span className={'text-red-500'}>*</span>
                </label>
              </>
            ) : (
              <label className={label}>Question #{props.index + 1}</label>
            )}
            <TextField
              type="text"
              onChange={changeQuestion}
              value={props.question}
              placeholder={props.questionPlaceholder}
              inputProps={{ maxLength: 200 }}
              className={validate('question')}
            />
          </div>
          <div className={styles.field}>
            {props.isSalesPageBuilder ? (
              <label className={label}>
                Answer <span className={'text-red-500'}>*</span>
              </label>
            ) : (
              <label className={label}>Answer #{props.index + 1}</label>
            )}
            <TextField
              multiline
              onChange={changeAnswer}
              value={props.answer}
              inputProps={{ maxLength: 2000, className: textarea }}
              placeholder={props.answerPlaceholder}
              className={validate('answer')}
            />
          </div>
        </div>
        <div
          className="faq-buttons"
          style={{
            /*background: '#0f0',*/ display: 'flex',
            flexGrow: '1',
            flexShrink: '1',
            justifyContent: 'flex-end',
          }}
        >
          {buttons[0]}
          {buttons[1]}
          {props.isSalesPageBuilder && props.total === 1 ? null : (
            <a
              className={clsx(styles.moveIcon, styles.iconRemove)}
              onClick={() => props.onDelete(props.index)}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default Faq;
